import {
  type ArtifactEventPayload,
  type ChatStepSlug,
  type DraftArtifactKind,
  STUDY_STEP_COUNT,
  type StudyStepSlug,
  type StudyStepView,
  previousStudyStep,
  nextStudyStep,
  isRunningStudyStep,
  studyStepNumber,
  threadStepOf,
} from '@sigora/domain';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type AudienceCatalog,
  type BriefReadView,
  type Study,
  fetchAudienceCatalog,
  fetchStudy,
  renameStudy,
  setStudyStep,
  stopTurn,
} from '../api/client.ts';
import { briefOnItsWay } from '../artifacts/fielding-stage.ts';
import { ANALYZING_CONTINUE_HINT, FIELDING_CONTINUE_HINT } from '../artifacts/launch-copy.ts';
import { useBriefSynthesis } from '../artifacts/use-brief-synthesis.ts';
import type { BriefNews } from './brief/use-brief-read.ts';
import { AUTO_ADVANCE_STEPS, autoAdvanceTarget, isReached } from './auto-advance.ts';
import { StepPanel } from './StepPanel.tsx';
import { StudySoFar } from './StudySoFar.tsx';
import { AskSigoraContext, type OpenAskSigora } from './ask-sigora.ts';
import { isApiError } from '../api/errors.ts';
import type { DraftStore } from '../chat/ChatPanel.tsx';
import { DeleteStudyControl } from './DeleteStudyControl.tsx';
import { type FollowUpScope, createFollowUpMemory } from './follow-ups/FollowUpThread.tsx';
import { type ComposerOffer, StepThread, type StepThreadHandle } from './StepThread.tsx';
import {
  type ContinueAction,
  type ContinueOutcome,
  FINISH_THIS_STEP_HINT,
  StepFooter,
  StepFooterContext,
  type StepFooterPrimary,
} from './step-footer.tsx';
import { AppHeader } from '../ui/AppHeader.tsx';
import { type HeaderAccount, KEEPS_MENU_OPEN } from '../ui/HeaderMenu.tsx';
import { type TooltipAlign, Tooltip } from '../ui/Tooltip.tsx';

export interface StudyShellProps {
  studyId: string;
  /** Back to the study list. */
  onBack: () => void;
  /** Which step is on screen, or nothing while the URL names the study and no step within it. */
  stepSlug?: StudyStepSlug;
  /** Go to a step, as a place the user asked for: it belongs in history. */
  onOpenStep: (studyId: string, stepSlug: StudyStepSlug) => void;
  /**
   * Correct the address to a step, without a history entry — a study opened with no step named,
   * and a step the study has not reached.
   */
  onReplaceStep: (studyId: string, stepSlug: StudyStepSlug) => void;
  /**
   * Who is signed in and the control that ends it, at the end of the header's menu. Handed in
   * because the session belongs to the app shell rather than to a study.
   */
  account: HeaderAccount;
}

/**
 * Which edge of a rail segment its tooltip lines up with: the three at each end keep theirs on
 * the screen by lining up with the end they are near.
 */
const railTooltipAlign = (index: number, count: number): TooltipAlign =>
  index < 3 ? 'start' : index >= count - 3 ? 'end' : 'center';

/**
 * What the footer's Continue is doing, for the step it was pressed on (`from`):
 *
 * - `running`: the step's write is out, under the press it was started by.
 * - `advancing`: the write was accepted; waiting for the first study read numbered `afterRead` or
 *   later.
 * - `moving`: that read showed the next step reached, and the move to it has been asked for.
 * - `held`: that read still had the next step closed, so the researcher stays and is told why,
 *   until a later read shows it reached.
 *
 * The first three show the button busy with `label`.
 */
type ContinueState =
  | { phase: 'idle' }
  | { phase: 'running'; press: number; from: StudyStepSlug; to: StudyStepSlug; label: string }
  | {
      phase: 'advancing';
      press: number;
      from: StudyStepSlug;
      to: StudyStepSlug;
      label: string;
      afterRead: number;
    }
  | { phase: 'moving'; from: StudyStepSlug; label: string }
  | { phase: 'held'; from: StudyStepSlug; to: StudyStepSlug };

const CONTINUE_IDLE: ContinueState = { phase: 'idle' };

/**
 * One study: its conversation, its documents and the controls over it.
 *
 * The study is the addressable unit — it is what the URL names and what this component is keyed
 * by — so everything here belongs to one study and nothing survives a move to another. A second
 * study is a second address, reached by navigating rather than by switching a picker.
 */
export const StudyShell = ({
  studyId,
  stepSlug,
  onBack,
  onOpenStep,
  onReplaceStep,
  account,
}: StudyShellProps) => {
  /**
   * The thread the conversation on screen is: the one the step the address bar names shows
   * (`threadStepOf`), and nothing only while the URL names no step, which the study read corrects
   * to a step of its own a moment later.
   *
   * The chat below is keyed by it, so each step's thread is its own transcript and its own sends,
   * and a step that shows another's thread keeps that thread mounted when the study moves between
   * them.
   */
  const threadStep: ChatStepSlug | null = stepSlug === undefined ? null : threadStepOf(stepSlug);
  const [study, setStudy] = useState<Study | null>(null);
  /**
   * Whether the server says there is no such study for this user.
   *
   * A study id in the address bar is whatever the browser was pointed at — a stale bookmark, a
   * link from somebody else's account — and both answer 404. Rendering the shell around a study
   * that is not there shows a header with no name and a sidebar of empty documents, which reads
   * as a study that exists and is blank.
   */
  const [notFound, setNotFound] = useState(false);
  /**
   * The panel's demographics, handed to the sidebar.
   *
   * Here rather than in the sidebar because the sidebar is keyed by study and remounts on every
   * switch, while the catalog is one list for the whole panel — reading it there would be the same
   * request again for every study the user looks at. Null covers both "not back yet" and "could
   * not be read", which the audience card treats alike: it shows the stored ids.
   *
   * What is held is the best answer any read has produced: an available catalog stands until
   * another available one replaces it, so the words on a card the researcher is looking at do not
   * come off because a later read found the panel unreachable.
   */
  const [audienceCatalog, setAudienceCatalog] = useState<AudienceCatalog | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  /**
   * How many study reads have been asked for, and which of them the study on screen came from.
   * Each read is asked for under the next number, and the one that lands records its own, so a
   * wait for "a read asked for after this moment" is a comparison rather than a guess about which
   * answer is which.
   */
  const readsAsked = useRef(0);
  const [readLanded, setReadLanded] = useState(0);
  const requestRead = useCallback(() => {
    readsAsked.current += 1;
    setRefreshToken(readsAsked.current);

    return readsAsked.current;
  }, []);
  /**
   * The panel's demographics, read at mount and again whenever a turn ends.
   *
   * Asked more than once because whether Remesh answers is a property of the moment rather than of
   * the page's lifetime: a tab opened while the panel was unreachable holds no labels, and the
   * thing that would put them there — the panel answering again — happens after the mount-time
   * read.
   *
   * A turn ending is the only other trigger, and what it is not is the point: no timer asks.
   * `handleStudyChanged` ticks on a clock — the launch panel bumps a study change every few
   * seconds while a study is provisioning, and the Fielding step while it fields or is analysed,
   * the last of them unbounded — and a catalog read on a timer is a Remesh request per tick for the life of a tab, worst of all while the panel is unreachable, which is the state this recovers
   * from. A turn is something the researcher did, so the reads are bounded by their work.
   *
   * `stillWanted` is checked after the read, which is what the mount effect hands it so that an
   * effect torn down mid-read writes nothing. The turn-end call passes none and takes the default:
   * a shell is keyed by study and remounts on a move, so the write it could make after an unmount
   * is a no-op on state nothing renders.
   */
  const loadAudienceCatalog = useCallback(
    async (stillWanted: () => boolean = () => true) => {
      // Labels are decoration on a document that reads without them, so a failed catalog read is
      // not worth an error banner over the whole study — it is one more answer of "nothing was
      // checked", which is what null says.
      const answer = await fetchAudienceCatalog(studyId).catch(() => null);

      if (!stillWanted()) {
        return;
      }

      // An available answer is never displaced by an unavailable one, compared against what is
      // held at the moment of the write: an outage says nothing about a panel that has already
      // been read, and a card that lost its words to one would be reporting a panel that disowns
      // the whole audience. That is the whole of what this orders. Two available answers are not
      // ordered against each other — whichever resolves last stands, whichever was asked for
      // first — and nothing rests on which of them does, since both describe the same panel.
      setAudienceCatalog((held) =>
        answer?.available === true || held?.available !== true ? answer : held,
      );
    },
    [studyId],
  );
  /**
   * Stable across renders, deliberately. This shell re-renders as a turn runs — the thread
   * reporting it running and ended, each document it streams, the study read when it ends — so an
   * inline arrow here would hand the launch panel a new callback each time. The panel does not
   * depend on this being stable, but a prop that changes identity on every render is a trap for
   * the next thing that does.
   */
  const handleStudyChanged = useCallback(() => {
    requestRead();
  }, [requestRead]);
  /** A step of this study, which the Launch step's blockers link to; stable for the same reason. */
  const openStudyStep = useCallback(
    (slug: StudyStepSlug) => onOpenStep(studyId, slug),
    [onOpenStep, studyId],
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  /**
   * What each step's composer holds, kept here rather than in the thread that draws it.
   *
   * Each step's thread is its own mount, so the composer is mounted and unmounted as the
   * researcher walks: a message half-written on one step and a detour to another is an ordinary
   * path through the study, and losing the words to it would be losing work nobody asked to
   * discard. Per step, because each step's conversation is its own and a message typed on one is
   * not a message for another. This shell is keyed by study, so the words go when the study does.
   *
   * A ref rather than state, because nothing up here renders them. State would make every
   * keystroke re-render the step, its documents and the whole transcript — the composer would be
   * the slowest thing on the screen on the longest conversations, which are exactly the ones worth
   * typing into.
   */
  const drafts = useRef(new Map<ChatStepSlug, string>());
  const draftStore = useMemo<DraftStore | null>(
    () =>
      threadStep === null
        ? null
        : {
            read: () => drafts.current.get(threadStep) ?? '',
            write: (next: string) => {
              drafts.current.set(threadStep, next);
            },
          },
    [threadStep],
  );
  /**
   * What the Brief's composer keeps beside its words — questions whose send failed and the ids they
   * went under, and the card the next question is about — kept here for the reason the words are:
   * the Brief's thread is unmounted as the researcher walks, and a question that was not sent is
   * work. A store the thread subscribes to rather than state, since a send answering after the step
   * was left writes into it.
   */
  const followUpMemory = useMemo(createFollowUpMemory, []);
  /**
   * Text to put back in each step's composer, and the one route by which anything does.
   *
   * A new object each time, because what matters is that a restore was asked for rather than what
   * it says — the same message handed back twice is two separate offers. Held here rather than in
   * the thread, because a send can come back after the researcher has left its step, and the words
   * then wait for that step to be drawn again rather than landing on whichever step is on screen.
   */
  const [restores, setRestores] = useState<Partial<Record<ChatStepSlug, ComposerOffer>>>({});
  /**
   * How many offers have been made. The composer takes up an offer it has not seen before, so the
   * count is what tells two offers of the same words apart — and what keeps a render that passes
   * the same offer again from refilling a composer that has already answered it.
   */
  const offers = useRef(0);
  const offer = useCallback(
    (
      step: ChatStepSlug,
      text: string,
      onTaken?: () => void,
      {
        reveal = false,
        scope,
        append = false,
      }: { reveal?: boolean; scope?: FollowUpScope; append?: boolean } = {},
    ) => {
      offers.current += 1;
      const token = offers.current;
      setRestores((current) => ({
        ...current,
        [step]: {
          text,
          token,
          ...(reveal ? { reveal } : {}),
          ...(scope === undefined ? {} : { scope }),
          ...(append ? { append } : {}),
          ...(onTaken === undefined ? {} : { onTaken }),
          /*
          Dropped once the composer has answered it, taken up or declined. An offer is answered
          for good, and the composer's own memory of that goes when it unmounts — so an offer left
          standing here would be made again to the next mount, putting back words the researcher
          may have cleared on purpose.
        */
          onAnswered: () =>
            setRestores((held) => {
              if (held[step]?.token !== token) {
                return held;
              }

              const { [step]: _answered, ...rest } = held;

              return rest;
            }),
        },
      }));
    },
    [],
  );
  /**
   * The study's published Brief once either Brief step has read it, so moving between the two
   * neither reads it again nor shows the loading line. Held here because the shell is keyed by
   * study: the read goes when the study does.
   */
  const briefCache = useRef<BriefReadView | null>(null);
  /**
   * What the Brief's follow-ups last said moved the Brief, for the Brief step to take: the read a
   * choice was answered with, or word to read the Brief again.
   */
  const [briefNews, setBriefNews] = useState<BriefNews | null>(null);
  /**
   * What a step's own controls put in its composer — the Brief's Ask about this, with the card the
   * question is about — which brings the composer to the reader whether or not it takes the words.
   */
  const askAbout = useCallback(
    (step: ChatStepSlug, text: string, scope?: FollowUpScope) =>
      offer(step, text, undefined, { reveal: true, ...(scope === undefined ? {} : { scope }) }),
    [offer],
  );
  /**
   * The thread on screen, which is what an approval's follow-up reaches. Null while the URL names
   * no step.
   */
  const thread = useRef<StepThreadHandle>(null);
  /**
   * The artifact parts the thread on screen has streamed, handed to the step's documents so a
   * document shows while the agent is still writing it.
   */
  const [streamed, setStreamed] = useState<ArtifactEventPayload[]>([]);
  /** Whether the Study so far sheet is open. The only overview in the product, and it is asked for. */
  const [sheetOpen, setSheetOpen] = useState(false);
  /**
   * The step this view has already told the server about, so a record that is in flight or landed
   * is not made again. The study read that would say it landed arrives a round trip later, and
   * the effect below runs on every render until it does.
   */
  const recordedStep = useRef<StudyStepSlug | null>(null);
  /**
   * Whether the Ask Sigora drawer is open. One for the whole study rather than per thread, so a
   * researcher reading the conversation while they walk the steps keeps it open as they go.
   */
  const [chatOpen, setChatOpen] = useState(false);
  /**
   * What a step's own controls ask of the drawer: open it on the thread on screen and hand that
   * thread the words, to offer or to send. Whether they can be sent is the thread's to decide, once
   * it knows what is running (see `ask-sigora.ts`), and a request it has not decided goes with it
   * when the researcher leaves the step. Stable, so a step holding it never re-renders for it.
   */
  const openAskSigora = useCallback<OpenAskSigora>(({ message, autoSend = false } = {}) => {
    const onScreen = thread.current;
    if (onScreen === null) {
      return;
    }

    setChatOpen(true);
    const text = message?.trim() ?? '';
    onScreen.ask(text, { send: autoSend && text !== '' });
  }, []);
  /**
   * What the step on screen registered for the footer's primary button, or null for a plain move
   * to the next step.
   */
  const [continueAction, setContinueAction] = useState<ContinueAction | null>(null);
  /** What Continue is doing on the step it was pressed on. */
  const [continuing, setContinuing] = useState<ContinueState>(CONTINUE_IDLE);
  /**
   * Counts Continue presses and step changes, so a write answering after either — Back and forward
   * again to the same step included — is recognised as no longer the one on screen.
   */
  const continueGeneration = useRef(0);
  /** Whether a turn is running in the conversation on screen. */
  const [threadRunning, setThreadRunning] = useState(false);
  /**
   * The step whose next step opened while the researcher was busy beside it — in the conversation,
   * or in Study so far — so moving on waits until they are done.
   */
  const [advanceDueFrom, setAdvanceDueFrom] = useState<StudyStepSlug | null>(null);
  /**
   * The step the last study read was seen on, and whether the step after it was closed then: what
   * tells a step opening while the researcher watches from one that was open when they arrived.
   */
  const watched = useRef<{ step: StudyStepSlug; nextLocked: boolean } | null>(null);

  /**
   * Tells the server to stop a turn.
   *
   * Stopping is a server-side act: aborting the local read only stops the listening, while the
   * worker goes on running the turn and paying for it, which is the opposite of what the button
   * says. That makes the id essential rather than convenient — without it the click cancels the
   * screen and nothing else.
   */
  const stopServerTurn = useCallback((turnId: string) => {
    void stopTurn(turnId).catch(() => {
      // The local abort has already happened; a failed cancel is worth saying, but not worth
      // blocking the user's own cancellation on.
      setLoadError('That turn could not be cancelled — it may still be running.');
    });
  }, []);

  /**
   * A turn the thread on screen was watching has ended.
   *
   * Stable, like `handleStudyChanged`, because the thread reports through it from inside the chat,
   * which re-renders with every publication of a streaming reply.
   */
  const handleTurnFinished = useCallback(() => {
    // The streamed parts said what the writes did; this is what re-reads what they left
    // behind, including anything the turn changed that it never streamed.
    requestRead();
    // The panel is re-read on the same beat, so a tab that opened while Remesh was unreachable
    // picks up its labels on the next thing the researcher does rather than on a reload.
    void loadAudienceCatalog();
  }, [loadAudienceCatalog, requestRead]);

  /**
   * A follow-up finalized on the Brief. A note or a log line moves only the Brief's record, which
   * the Brief step alone draws, so it is handed the read the choice was answered with — the same
   * version as the one on the page — and nothing else is read. An update writes a version, which
   * the study's other reads describe too, and a finalize a read found, with no read in hand, may be
   * one; either is read again as a turn's end is. Stable, for the thread that reports through it.
   */
  const handleBriefChanged = useCallback(
    (read: BriefReadView | null) => {
      const shown = briefCache.current?.brief?.version ?? null;
      if (read !== null && read.brief !== null && read.brief.version === shown) {
        setBriefNews((current) => ({ seq: (current?.seq ?? 0) + 1, read }));
        return;
      }
      handleTurnFinished();
    },
    [handleTurnFinished],
  );

  useEffect(() => {
    let active = true;

    void loadAudienceCatalog(() => active);

    return () => {
      active = false;
    };
  }, [loadAudienceCatalog]);

  useEffect(() => {
    let active = true;
    const asked = refreshToken;
    void fetchStudy(studyId).then(
      (loaded) => {
        if (active) {
          setStudy(loaded);
          setReadLanded(asked);
        }
      },
      (failure: unknown) => {
        if (!active) {
          return;
        }

        // Landed, with nothing in it: a Continue waiting on this read stops waiting.
        setReadLanded(asked);
        // The sidebar reads state off this; leaving it null renders the study as not yet readable
        // rather than as a draft, which is the honest answer when the read failed.
        setStudy(null);

        // A 404 is not a failed read, it is an answer: there is no such study for this user, and
        // the screen for that is a screen of its own rather than an error line over an empty
        // shell.
        if (isApiError(failure) && failure.code === 'study_not_found') {
          setNotFound(true);

          return;
        }

        setLoadError(
          failure instanceof Error ? failure.message : 'This study could not be opened.',
        );
      },
    );

    return () => {
      active = false;
    };
  }, [studyId, refreshToken]);

  /**
   * Read off this study's own row or not at all. The read lags this shell by a round trip on the
   * first render, and everything drawn from it has to be about the study on screen — a row carried
   * over from anywhere else would describe another study.
   */
  const loadedStudy = study !== null && study.id === studyId ? study : null;

  /**
   * The heading, which is the study's own title once the read has landed.
   *
   * A placeholder until then rather than an empty heading: the shell renders before the read
   * answers, and a blank line where the name goes reads as a study with no name.
   */
  const title = loadedStudy?.title ?? 'Loading…';

  /**
   * The ten steps as the server derived them, or nothing until the study has been read. Empty
   * rather than a guess: every status on this list is a fact about the study, and inventing one
   * before the study is known is the kind of confident wrong answer the shell exists to avoid.
   */
  const steps: readonly StudyStepView[] = loadedStudy?.steps ?? [];
  /** The step on screen, once there is one to show. */
  const step = steps.find((candidate) => candidate.slug === stepSlug) ?? null;

  /**
   * The Brief request while the study is analysed and its analysis not given up on, on the Analyzing
   * step: read here, once, so the step's headline and status line and the Next hint below are all
   * decided from the same read. No other step shows the request, so none reads it.
   */
  const escalation = loadedStudy?.readinessEscalation ?? null;
  const brief = useBriefSynthesis({
    studyId,
    refreshToken,
    active: loadedStudy?.state === 'analyzing' && escalation === null && step?.slug === 'analyzing',
    onStudyChanged: handleStudyChanged,
  });

  /**
   * The address bar, corrected to a step the study has.
   *
   * Two corrections, both replacements rather than pushes: a study opened with no step named goes
   * to the one it remembers, and a step the study has not reached goes there too — a bookmark
   * pointing ahead of the work is stale rather than wrong, and refusing it outright would make a
   * shared link a dead end. Neither is a place the user asked for, so neither belongs in history.
   */
  useEffect(() => {
    if (loadedStudy === null) {
      return;
    }

    if (stepSlug === undefined) {
      onReplaceStep(studyId, loadedStudy.currentStep);

      return;
    }

    const requested = loadedStudy.steps.find((candidate) => candidate.slug === stepSlug);
    if (requested?.status === 'locked') {
      onReplaceStep(studyId, loadedStudy.currentStep);
    }
  }, [loadedStudy, stepSlug, studyId, onReplaceStep]);

  /**
   * The study remembers where its owner is, so a refresh or a return tomorrow lands here.
   *
   * Recorded from wherever they navigate to rather than only from a Continue, because Back is a
   * move too and a study that remembered only forward motion would send someone who stepped back
   * and closed the tab to a step they had deliberately left.
   *
   * Nothing is recorded once a study has been paid for: from there its own lifecycle decides where
   * it opens — fielding, then the brief — whatever the column says, so a record would be written
   * and then contradicted by the next read, on every move, for ever.
   */
  useEffect(() => {
    if (
      loadedStudy === null ||
      stepSlug === undefined ||
      loadedStudy.state === 'complete' ||
      (loadedStudy.lockedAt ?? loadedStudy.paidAt) != null ||
      loadedStudy.currentStep === stepSlug ||
      recordedStep.current === stepSlug
    ) {
      return;
    }

    const requested = loadedStudy.steps.find((candidate) => candidate.slug === stepSlug);
    if (requested === undefined || requested.status === 'locked') {
      return;
    }

    recordedStep.current = stepSlug;
    void setStudyStep(studyId, stepSlug).then(
      // The study is read again rather than patched locally: the step decides every other step's
      // status, and a list left as it was would offer a Continue the server would refuse.
      () => handleStudyChanged(),
      () => {
        // Nothing to tell the researcher. The step they asked for is on screen either way; what
        // failed is the study's memory of it, and the next move asks again.
        recordedStep.current = null;
      },
    );
  }, [loadedStudy, stepSlug, studyId, handleStudyChanged]);

  /**
   * What Continue was doing belongs to the step it was pressed on: moving to another step, by
   * Continue or any other way, ends it.
   */
  useEffect(() => {
    continueGeneration.current += 1;
    setContinuing(CONTINUE_IDLE);
    setAdvanceDueFrom(null);
  }, [stepSlug]);

  /**
   * Moves on after a write Continue ran, once the first study read asked for after the server
   * accepted it has landed — and only if that read shows the next step reached. Waiting for the read
   * rather than moving at once is what keeps the address correction above from ever seeing a step
   * the held study calls closed. A read that still has it closed keeps the researcher here and
   * says why, until a later read shows the next step reached. A read that failed keeps them here
   * too, with the shell's own error rather than a hint about the step, whose write was accepted. A
   * move to another step clears it before then (above).
   */
  useEffect(() => {
    if (continuing.phase === 'held') {
      if (isReached(loadedStudy?.steps ?? [], continuing.to)) {
        setContinuing(CONTINUE_IDLE);
      }

      return;
    }

    if (continuing.phase !== 'advancing' || readLanded < continuing.afterRead) {
      return;
    }

    if (loadedStudy === null) {
      setContinuing(CONTINUE_IDLE);

      return;
    }

    if (isReached(loadedStudy.steps, continuing.to)) {
      // The button stays busy until the step changes, which clears it.
      setContinuing({ phase: 'moving', from: continuing.from, label: continuing.label });
      onOpenStep(studyId, continuing.to);

      return;
    }

    setContinuing({ phase: 'held', from: continuing.from, to: continuing.to });
  }, [continuing, loadedStudy, readLanded, studyId, onOpenStep]);

  /**
   * Moves on by itself from a step the researcher is watching finish (`AUTO_ADVANCE_STEPS`): Launch
   * once the study is launched, Fielding once its answers are being read, Analyzing once its Brief
   * is written. Only on a change seen while on the step — the read before had the next step closed
   * and this one has it open — so arriving on a step whose next one is already open, Back from the
   * Brief included, never bounces forward. A push rather than a replace, so Back returns to the
   * step that was being watched.
   *
   * Seeing the change makes the move due; the effect after this one makes it.
   */
  useEffect(() => {
    if (loadedStudy === null || stepSlug === undefined) {
      return;
    }

    const next = nextStudyStep(stepSlug);
    const nextLocked = !isReached(loadedStudy.steps, next);
    const seen = watched.current;
    watched.current = { step: stepSlug, nextLocked };

    if (
      next === null ||
      !AUTO_ADVANCE_STEPS.has(stepSlug) ||
      seen?.step !== stepSlug ||
      !seen.nextLocked ||
      nextLocked
    ) {
      return;
    }

    setAdvanceDueFrom(stepSlug);
  }, [loadedStudy, stepSlug]);

  /**
   * Makes a due move, but not under a researcher who is busy beside the step: not while the Ask
   * Sigora drawer is open or a turn is running in the conversation, and not while Study so far is
   * open. Once they are done it moves only if they are still on that step and the next one is
   * still open, and on past every step that read has already taken the study beyond
   * (`autoAdvanceTarget`). The footer's Next moves them meanwhile, as it always does.
   */
  const chatInUse = chatOpen || threadRunning;
  useEffect(() => {
    if (advanceDueFrom === null || advanceDueFrom !== stepSlug || sheetOpen || chatInUse) {
      return;
    }

    setAdvanceDueFrom(null);
    const target = autoAdvanceTarget(loadedStudy?.steps ?? [], stepSlug);
    if (target !== null) {
      onOpenStep(studyId, target);
    }
  }, [advanceDueFrom, stepSlug, sheetOpen, chatInUse, loadedStudy, studyId, onOpenStep]);

  /**
   * An approval the server answered with a turn: the agent is already drafting what comes next,
   * written into the thread of the step whose card was approved — the thread on screen.
   */
  const handleApproved = ({ kind, turnId }: { kind: DraftArtifactKind; turnId: string | null }) => {
    if (turnId === null) {
      return;
    }

    thread.current?.adoptApproval({ kind, turnId });
  };

  if (notFound) {
    return (
      <>
        <AppHeader account={account} />
        <main className="studies" data-test-id="study-not-found">
          <div className="studies-header">
            <h1>Not found</h1>
          </div>
          <p role="alert">That study is not one of yours.</p>
          <button type="button" data-test-id="back-to-studies" onClick={onBack}>
            All studies
          </button>
        </main>
      </>
    );
  }

  const stepBefore = stepSlug === undefined ? null : previousStudyStep(stepSlug);
  const stepAfter = stepSlug === undefined ? null : nextStudyStep(stepSlug);
  /** Whether the study may go on, which is the same question the server answers on the record. */
  const canContinue = stepAfter !== null && isReached(steps, stepAfter);
  /** Once the study is running there is nothing to settle on a step, only the next one to see. */
  const nextLabel = stepSlug !== undefined && isRunningStudyStep(stepSlug) ? 'Next' : 'Continue';
  const shown = continuing.phase === 'idle' || continuing.from !== stepSlug ? null : continuing;
  const busyLabel = shown === null || shown.phase === 'held' ? null : shown.label;

  /** Runs the step's own write, and on its acceptance waits for the read that says where to go. */
  const runContinue = (action: ContinueAction, from: StudyStepSlug, to: StudyStepSlug) => {
    continueGeneration.current += 1;
    const press = continueGeneration.current;
    const label = action.busyLabel ?? action.label ?? nextLabel;
    setContinuing({ phase: 'running', press, from, to, label });
    // The contract is that `run` never throws or rejects; one that does has shown nothing and
    // moves nothing. Called at once rather than a tick later, so the step's write starts with the
    // press.
    let written: Promise<ContinueOutcome>;
    try {
      written = action.run();
    } catch {
      written = Promise.resolve('stay');
    }
    void written
      .catch(() => 'stay' as const)
      .then((outcome) => {
        // Another press, or a move to another step since — even back to this one — has taken over.
        if (continueGeneration.current !== press) {
          return;
        }

        setContinuing(
          outcome === 'stay'
            ? CONTINUE_IDLE
            : { phase: 'advancing', press, from, to, label, afterRead: requestRead() },
        );
      });
  };

  /** The footer's primary button: absent on the last step, and otherwise the step's or a move. */
  const primary: StepFooterPrimary | null =
    stepAfter === null || stepSlug === undefined
      ? null
      : busyLabel !== null
        ? {
            label: busyLabel,
            disabled: true,
            busy: true,
            hint: null,
            onClick: () => undefined,
          }
        : continueAction !== null
          ? {
              label: continueAction.label ?? nextLabel,
              disabled: continueAction.disabled,
              busy: false,
              hint: continueAction.disabled
                ? (continueAction.blockedHint ?? FINISH_THIS_STEP_HINT)
                : shown?.phase === 'held'
                  ? FINISH_THIS_STEP_HINT
                  : null,
              onClick: () => runContinue(continueAction, stepSlug, stepAfter),
            }
          : {
              label: nextLabel,
              disabled: !canContinue,
              busy: false,
              hint:
                canContinue || step === null
                  ? null
                  : isRunningStudyStep(step.slug)
                    ? /*
                        A running step has nothing for the researcher to finish, so it says only
                        what Next is waiting on, and nothing once nothing more is coming: an ended
                        study, an escalation or a failed Brief says why on the step itself.
                      */
                      step.slug === 'fielding' && loadedStudy?.state === 'live'
                      ? FIELDING_CONTINUE_HINT
                      : step.slug === 'analyzing' &&
                          briefOnItsWay({ state: loadedStudy?.state ?? null, escalation, brief })
                        ? ANALYZING_CONTINUE_HINT
                        : null
                    : FINISH_THIS_STEP_HINT,
              onClick: () => onOpenStep(studyId, stepAfter),
            };

  return (
    <div
      className={`shell${threadStep !== null && chatOpen ? ' chat-open' : ''}`}
      data-test-id="study-shell"
    >
      <AppHeader
        menuLabel="Study options"
        leading={
          <>
            <h1 data-test-id="study-title">{title}</h1>
            <button
              type="button"
              className="step-indicator"
              aria-haspopup="dialog"
              aria-expanded={sheetOpen}
              data-test-id="step-indicator"
              disabled={step === null}
              onClick={() => setSheetOpen((open) => !open)}
            >
              {/* The step's name is on the rail below, under its segment. */}
              {step === null
                ? 'Opening…'
                : `Step ${studyStepNumber(step.slug)} of ${STUDY_STEP_COUNT}`}
            </button>
          </>
        }
        menuItems={
          <>
            <button type="button" data-test-id="back-to-studies" onClick={onBack}>
              All studies
            </button>
            <button
              type="button"
              data-test-id="rename-study"
              /**
               * Disabled until the study has been read, because the prompt opens on its current title:
               * a rename offered over a title nobody has seen would ask the researcher to edit a
               * blank. And disabled past draft, because the server refuses it there — the title is on
               * an accepted quote and on a conversation Remesh already holds under that name — so an
               * enabled control would be one whose only outcome is an error.
               */
              disabled={loadedStudy === null || loadedStudy.state !== 'draft'}
              onClick={() => {
                const next = globalThis.prompt('Study title', title)?.trim();
                if (next === undefined || next === '' || next === title) {
                  return;
                }

                setLoadError(null);
                void renameStudy(studyId, next).then(
                  (renamed) =>
                    setStudy((current) =>
                      current === null ? current : { ...current, title: renamed.title },
                    ),
                  (failure: unknown) => {
                    setLoadError(
                      failure instanceof Error
                        ? failure.message
                        : 'The study could not be renamed.',
                    );
                  },
                );
              }}
            >
              Rename
            </button>

            {/*
                Offered only when the server says a delete would be accepted, which folds in the
                checkout hold the browser cannot see. It asks to be confirmed inside the menu, so
                pressing it keeps the menu open. Deleting leaves nothing to render, so it navigates
                back to the list — which reads the studies again on arrival.
              */}
            {loadedStudy?.deletable === true && (
              <div {...KEEPS_MENU_OPEN}>
                <DeleteStudyControl
                  studyId={studyId}
                  title={title}
                  testId="delete-study"
                  onDeleted={onBack}
                />
              </div>
            )}
          </>
        }
        account={account}
      >
        {/*
          Every step in order as a segment, with the one on screen marked and named under it.
          Every segment is a button with a tooltip naming its step and saying what happens on it.
          A step the study has reached opens from its segment, as it does from the sheet; the
          step on screen and a step not reached yet open nothing and say so. The steps that run
          once the study is launched are set apart from the ones that set it up.
        */}
        <ol className="step-rail" data-test-id="step-rail" aria-label="Study steps">
          {steps.map((item, index) => {
            const current = item.slug === stepSlug;
            const standing = current
              ? 'current'
              : item.status === 'complete'
                ? 'complete'
                : 'ahead';
            const opens = !current && item.status !== 'locked';

            return (
              <li
                key={item.slug}
                aria-current={current ? 'step' : undefined}
                className={`step-rail-item step-rail-${standing}${
                  isRunningStudyStep(item.slug) ? ' step-rail-item-running' : ''
                }`}
                data-test-id={`step-rail-${item.slug}`}
              >
                <Tooltip
                  align={railTooltipAlign(index, steps.length)}
                  className="step-rail-tooltip"
                  testId={`step-rail-tooltip-${item.slug}`}
                  heading={item.name}
                  content={item.about}
                >
                  <button
                    type="button"
                    className="step-rail-target"
                    data-test-id={`step-rail-target-${item.slug}`}
                    aria-disabled={opens ? undefined : 'true'}
                    onClick={opens ? () => onOpenStep(studyId, item.slug) : undefined}
                  >
                    <span aria-hidden="true" className="step-rail-segment" />
                    {current ? (
                      <span className="step-rail-label">{item.name}</span>
                    ) : (
                      <span className="visually-hidden">
                        {item.name}
                        {standing === 'complete' && ', done'}
                      </span>
                    )}
                  </button>
                </Tooltip>
              </li>
            );
          })}
        </ol>
      </AppHeader>

      <main className="step-shell" data-test-id="step-shell">
        {loadError !== null && (
          <p className="chat-error" data-test-id="shell-error" role="alert">
            {loadError}
          </p>
        )}
        {sheetOpen && step !== null && (
          <StudySoFar
            steps={steps}
            stepSlug={step.slug}
            onOpenStep={(slug) => {
              setSheetOpen(false);
              onOpenStep(studyId, slug);
            }}
            onClose={() => setSheetOpen(false)}
          />
        )}

        {step === null ? (
          <p data-test-id="step-loading">Opening this study…</p>
        ) : (
          <StepFooterContext.Provider value={setContinueAction}>
            <AskSigoraContext.Provider value={openAskSigora}>
              <StepPanel
                step={step}
                studyId={studyId}
                study={study}
                streamed={streamed}
                audienceCatalog={audienceCatalog}
                refreshToken={refreshToken}
                onStudyChanged={handleStudyChanged}
                onApproved={handleApproved}
                onOpenStep={openStudyStep}
                offer={askAbout}
                briefCache={briefCache}
                brief={brief}
                briefNews={briefNews}
              />
            </AskSigoraContext.Provider>
          </StepFooterContext.Provider>
        )}

        {/*
          The conversation, on every step: the thread `threadStepOf` names for it, which is the
          domain's answer rather than a list of slugs here.
        */}
        {/*
          Keyed by study and step, so each step's conversation is its own mount: moving to another
          step leaves this one's transcript, sends and running read behind, and coming back reads
          them again.
        */}
        {threadStep !== null && draftStore !== null && (
          <StepThread
            key={`${studyId}/${threadStep}`}
            ref={thread}
            studyId={studyId}
            step={threadStep}
            screen={stepSlug}
            draftStore={draftStore}
            restore={restores[threadStep] ?? null}
            offer={offer}
            stopServerTurn={stopServerTurn}
            onTurnFinished={handleTurnFinished}
            onBriefChanged={handleBriefChanged}
            followUpMemory={followUpMemory}
            onStreamed={setStreamed}
            expanded={chatOpen}
            onExpandedChange={setChatOpen}
            onRunningChange={setThreadRunning}
          />
        )}
      </main>

      <StepFooter
        onBack={() => (stepBefore === null ? onBack() : onOpenStep(studyId, stepBefore))}
        primary={primary}
      />
    </div>
  );
};
